/**
 * <bodi-countdown> — live launch countdown timer.
 *
 * Reads an ISO 8601 target datetime from its `data-launch` attribute and ticks
 * down Days / Hours / Minutes / Seconds once per second. Designed to fail
 * gracefully: if the date is missing, unparseable, or already in the past it
 * shows a static "Launching soon" fallback instead of a broken or negative
 * timer. No dependencies.
 *
 * Usage:
 *   <bodi-countdown data-launch="2026-09-01T00:00:00+12:00">
 *     <div data-countdown-timer>
 *       <span data-unit="days">--</span>
 *       <span data-unit="hours">--</span>
 *       <span data-unit="minutes">--</span>
 *       <span data-unit="seconds">--</span>
 *     </div>
 *     <div data-countdown-fallback class="hidden">Launching soon</div>
 *   </bodi-countdown>
 *
 * Attributes:
 *   data-launch — target datetime as an ISO 8601 string. If blank / invalid /
 *                 in the past, the fallback is shown and no interval starts.
 *
 * Child hooks (all optional — missing nodes are tolerated):
 *   [data-countdown-timer]    — wrapper for the live numeric cells
 *   [data-countdown-fallback] — the "Launching soon" message (hidden by default)
 *   [data-unit="days|hours|minutes|seconds"] — the numeric value cells
 */
/**
 * Parse an ISO 8601 datetime to epoch ms in a cross-browser-safe way.
 * Safari (notably iOS < 16) mishandles a numeric timezone offset like
 * "2026-09-01T00:00:00+12:00" (returns NaN or treats it as UTC). We parse the
 * offset manually so every browser agrees on the instant. Bare "Z" / date-only
 * strings fall through to the native parser.
 * @returns {number|null} epoch milliseconds, or null if unparseable
 */
function bodiParseIso(raw) {
  if (!raw) return null;
  const m = raw.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})([+-])(\d{2}):(\d{2})$/);
  if (m) {
    const base = new Date(m[1] + 'Z').getTime();
    if (Number.isNaN(base)) return null;
    const sign = m[2] === '+' ? -1 : 1;
    const offsetMs = sign * (parseInt(m[3], 10) * 60 + parseInt(m[4], 10)) * 60000;
    return base + offsetMs;
  }
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? null : t;
}

class BodiCountdown extends HTMLElement {
  connectedCallback() {
    /* Re-entrancy guard: the Theme Editor can re-run connectedCallback on
       re-order/toggle without disconnecting first — kill any prior interval. */
    this.clear();

    this.timerEl = this.querySelector('[data-countdown-timer]');
    this.fallbackEl = this.querySelector('[data-countdown-fallback]');
    this.units = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    const raw = this.getAttribute('data-launch');
    const target = bodiParseIso(raw);

    /* Bail to fallback if the date is missing, unparseable, or already past. */
    if (target === null || target <= Date.now()) {
      this.showFallback();
      return;
    }

    this.target = target;

    /* Paint once immediately so there's no visible delay before the first tick. */
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    this.clear();
  }

  clear() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  tick() {
    const remaining = this.target - Date.now();

    /* Reached or passed zero — stop and switch to the fallback. */
    if (remaining <= 0) {
      this.clear();
      this.showFallback();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.set('days', String(days));
    this.set('hours', this.pad(hours));
    this.set('minutes', this.pad(minutes));
    this.set('seconds', this.pad(seconds));
  }

  set(unit, value) {
    const el = this.units[unit];
    if (el) {
      el.textContent = value;
    }
  }

  pad(value) {
    return String(value).padStart(2, '0');
  }

  showFallback() {
    if (this.timerEl) {
      this.timerEl.classList.add('hidden');
    }
    if (this.fallbackEl) {
      this.fallbackEl.classList.remove('hidden');
    }
  }
}

if (!customElements.get('bodi-countdown')) {
  customElements.define('bodi-countdown', BodiCountdown);
}
